'use client';

import { forwardRef, createContext, useContext } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Switch } from '@/shared/ui/shadcn/switch';

// Context for settings display options (e.g., hide icons in compact mode).
interface SettingsDisplayContextValue {
  hideIcons?: boolean;
}

const SettingsDisplayContext = createContext<SettingsDisplayContextValue>({});

export function SettingsDisplayProvider({
  children,
  hideIcons = false,
}: {
  children: React.ReactNode;
  hideIcons?: boolean;
}) {
  return (
    <SettingsDisplayContext.Provider value={{ hideIcons }}>
      {children}
    </SettingsDisplayContext.Provider>
  );
}

export function useSettingsDisplay() {
  return useContext(SettingsDisplayContext);
}

type SettingsIcon = React.ComponentType<{
  className?: string;
  strokeWidth?: number;
}>;

interface SettingsItemToggle {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  ariaLabel?: string;
}

interface SettingsItemProps {
  icon?: SettingsIcon;
  title: string;
  /** Optional element rendered right after the title (e.g. a PRO badge). */
  titleAdornment?: React.ReactNode;

  /** Trailing muted value text/content (push/value rows). Ignored when a
   *  `toggle` is set. `valueContent` takes precedence for rich trailing
   *  content (swatch strip, thumbnail). */
  value?: React.ReactNode;
  valueContent?: React.ReactNode;
  /** Stacked multi-line subtitle rendered BELOW the title (rare). */
  subtitleLines?: string[];
  /**
   * Rich block rendered BELOW the title, in the same slot as `subtitleLines`.
   *
   * The trailing `value` / `valueContent` slot shares the title's line and
   * therefore truncates — fine for "Auto" or a palette name, useless for a
   * selection the user has to read in full (several picked products, each with
   * a manufacturer's own long name). Content placed here owns the width and
   * wraps instead of being cut off.
   */
  stackedContent?: React.ReactNode;

  /** Switch config — turns the row into a toggle row (no chevron). */
  toggle?: SettingsItemToggle;
  /** When a toggle is present, make the whole row flip the switch on
   *  click/Enter. Off → only the Switch itself is interactive (no row hover). */
  rowTogglesSwitch?: boolean;

  /** Blue "unsaved changes" dot next to the title. */
  hasChanges?: boolean;
  /** Amber pulsing dot signalling the row needs attention (empty required field). */
  needsAttention?: boolean;

  onClick?: () => void;
  disabled?: boolean;
  /** Show the trailing chevron (default true; always hidden for toggle rows). */
  showChevron?: boolean;
  /** Render-side compact inspector rows pass `px-4`; the default groups
   *  (estimate / album / account) use the wider `px-5` gutter. */
  paddingClassName?: string;
  /** Render a trailing hairline divider (saves callers a manual element). */
  divider?: boolean;
  hideIcon?: boolean;
  className?: string;
}

/**
 * The single settings row primitive — iOS/Android style menu item shared by
 * every settings surface (render inspector, album, estimate, account). Covers
 * push rows (chevron), value rows (trailing muted text/content), and toggle
 * rows (Switch). Keeping ONE implementation here is what keeps the label font
 * (`text-list-item-title`) and chrome from drifting per panel.
 */
export const SettingsItem = forwardRef<HTMLDivElement, SettingsItemProps>(
  (
    {
      icon: Icon,
      title,
      titleAdornment,
      value,
      valueContent,
      subtitleLines,
      stackedContent,
      toggle,
      rowTogglesSwitch = false,
      hasChanges = false,
      needsAttention = false,
      onClick,
      disabled = false,
      showChevron = true,
      paddingClassName = 'px-5 py-3',
      divider = false,
      hideIcon = false,
      className,
    },
    ref
  ) => {
    const { hideIcons: hideIconsFromContext } = useSettingsDisplay();
    const shouldHideIcon = hideIcon || hideIconsFromContext;

    const iconEl =
      !shouldHideIcon && Icon ? (
        <Icon className="h-5 w-5 text-primary flex-shrink-0" strokeWidth={1.5} />
      ) : null;

    const titleRow = (
      <div className="flex flex-1 min-w-0 items-center gap-2">
        <span className="text-list-item-title text-foreground whitespace-nowrap">
          {title}
        </span>
        {titleAdornment}
        {hasChanges && (
          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
        )}
        {needsAttention && (
          <span className="w-2 h-2 rounded-full bg-warning flex-shrink-0 animate-attention-pulse" />
        )}
        {valueContent ? (
          <div className="flex-1 min-w-0 flex justify-end">{valueContent}</div>
        ) : (
          value != null && (
            <span className="text-list-item-subtitle text-muted-foreground flex-1 min-w-0 truncate text-end">
              {value}
            </span>
          )
        )}
      </div>
    );

    // A stacked block makes the row tall, and a vertically centred icon and
    // chevron then float in the middle of it — they belong beside the title.
    const isStacked = !!stackedContent;

    const body = (
      <div
        className={cn(
          'flex gap-3',
          isStacked ? 'items-start' : 'items-center',
          paddingClassName
        )}
      >
        {isStacked ? <div className="pt-0.5">{iconEl}</div> : iconEl}
        <div className="flex-1 min-w-0">
          {titleRow}
          {subtitleLines && subtitleLines.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-1">
              {subtitleLines.map((line, i) => (
                <p
                  key={i}
                  className="text-list-item-subtitle text-muted-foreground line-clamp-2"
                >
                  {line}
                </p>
              ))}
            </div>
          )}
          {stackedContent && <div className="mt-1.5">{stackedContent}</div>}
        </div>

        {toggle ? (
          // Stop propagation so tapping the switch never double-toggles via the
          // row's own click handler.
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={toggle.checked}
              onCheckedChange={toggle.onCheckedChange}
              disabled={disabled}
              aria-label={toggle.ariaLabel ?? title}
            />
          </div>
        ) : (
          showChevron && (
            <ChevronRight
              className={cn(
                'h-4 w-4 text-muted-foreground/70 flex-shrink-0 rtl:-scale-x-100',
                isStacked && 'mt-1'
              )}
              strokeWidth={2.5}
            />
          )
        )}
      </div>
    );

    // A row is interactive when it has an onClick, or when it's a toggle row
    // flagged to flip on row tap. Pure switch-only rows (section toggles) are
    // inert containers — only the Switch reacts, so a disabled state there dims
    // just the Switch (handled below), not the whole row.
    const rowToggles = !!toggle && rowTogglesSwitch;
    const isActionable = !!onClick || rowToggles;
    const isInteractive = isActionable && !disabled;
    const handleActivate = () => {
      if (disabled) return;
      if (rowToggles && toggle) toggle.onCheckedChange(!toggle.checked);
      else onClick?.();
    };

    return (
      <>
        <div
          ref={ref}
          className={cn(
            'transition-colors',
            isInteractive &&
              'cursor-pointer hover:bg-muted/50 active:bg-muted/70',
            disabled && isActionable && 'opacity-50 pointer-events-none',
            className
          )}
          {...(isInteractive
            ? {
                role: 'button' as const,
                tabIndex: 0,
                onClick: handleActivate,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                  }
                },
              }
            : {})}
        >
          {body}
        </div>
        {divider && <div className="border-t border-border/30 mx-5" />}
      </>
    );
  }
);

SettingsItem.displayName = 'SettingsItem';
